import { lazy, Suspense, useEffect } from 'react';
import type { FeatureFlagKey } from '@prakash-software/shared';
import { HashRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { useAgentStore, republishAvailability } from './stores/agent';
import { refreshFeatureFlags, useFeatureFlag, useFeatureFlagsStore } from './stores/feature-flags';
import { hydrateFromServer } from './lib/hydration';
import { onBusConnectionChange } from './lib/bus-connection';
import { resumeOrphans } from './lib/recording/uploader';
import { refreshRecordings } from './lib/recordings-api';
import { Shell } from './components/Shell';
import { Landing } from './routes/Landing';
import { Login } from './routes/Login';
import { ForgotPassword } from './routes/ForgotPassword';
import { ResetPassword } from './routes/ResetPassword';
import { Signup } from './routes/Signup';
import { VerifyEmail } from './routes/VerifyEmail';
import { Onboarding } from './routes/Onboarding';
import { Dashboard } from './routes/Dashboard';
import { Settings } from './routes/Settings';
import { Conversation } from './routes/Conversation';
import { Analytics } from './routes/Analytics';
import { Contacts } from './routes/Contacts';
import { Commands } from './routes/Commands';
import { AdminShell } from './routes/admin/AdminShell';
import { AdminSites } from './routes/admin/AdminSites';
import { AdminSiteDetail } from './routes/admin/AdminSiteDetail';
// The canvas library is large; load it only when a flow is opened.
const FlowBuilder = lazy(() => import('./routes/admin/FlowBuilder').then((m) => ({ default: m.FlowBuilder })));
import { AdminSystem } from './routes/admin/AdminSystem';
import { AdminLogs } from './routes/admin/AdminLogs';
import { AdminHealth } from './routes/admin/AdminHealth';
import { AdminJobs } from './routes/admin/AdminJobs';
import { AdminAccounts } from './routes/admin/AdminAccounts';
import { AdminBilling } from './routes/admin/AdminBilling';
import { AdminApiKeys } from './routes/admin/AdminApiKeys';
import { AdminDevelopers } from './routes/admin/AdminDevelopers';
import { AdminAudit } from './routes/admin/AdminAudit';
import { Tickets } from './routes/Tickets';
import { AdminAgents } from './routes/admin/AdminAgents';
import { AdminWhatsApp } from './routes/admin/AdminWhatsApp';
import { AdminFeatureFlags } from './routes/admin/AdminFeatureFlags';
import { AdminUsage } from './routes/admin/AdminUsage';

export function App() {
  return (
    <HashRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <Routes>
        {/* Public landing — entry point for unauthenticated visitors AND
            for signed-in agents (CTA swaps to "Continue to dashboard"). */}
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<RedirectIfLoggedIn page={<Login />} />} />
        {/* Reset flow stays reachable while signed in: the emailed link may be
            opened in a browser that still holds a stale session, and that
            session is about to be revoked anyway. */}
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/signup" element={<RedirectIfLoggedIn page={<Signup />} />} />
        {/* Reachable signed in too, like the reset link: the emailed link may
            open in a browser that already holds a session. */}
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route element={<RequireAuth />}>
          <Route element={<Shell />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/conversation/:id" element={<Conversation />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/commands" element={<Commands />} />
            <Route element={<RequireFeature flag="tickets" />}>
              <Route path="/tickets" element={<Tickets />} />
              <Route path="/tickets/:id" element={<Tickets />} />
            </Route>
            <Route path="/analytics" element={<RequireInsights />}>
              <Route index element={<Analytics />} />
            </Route>
            <Route path="/contacts" element={<RequireInsights />}>
              <Route index element={<Contacts />} />
            </Route>
            <Route path="/admin" element={<RequireAdmin />}>
              <Route element={<AdminShell />}>
                <Route index element={<Navigate to="sites" replace />} />
                <Route path="sites" element={<AdminSites />} />
                <Route path="sites/:siteId" element={<AdminSiteDetail />} />
                <Route element={<RequireFeature flag="flows" />}>
                  <Route
                    path="sites/:siteId/flows/:flowId"
                    element={
                      <Suspense fallback={null}>
                        <FlowBuilder />
                      </Suspense>
                    }
                  />
                </Route>
                <Route path="agents" element={<AdminAgents />} />
                <Route path="usage" element={<AdminUsage />} />
                <Route path="billing" element={<AdminBilling />} />
                <Route path="api-keys" element={<AdminApiKeys />} />
                <Route path="developers" element={<AdminDevelopers />} />
                <Route path="audit" element={<AdminAudit />} />
                <Route element={<RequirePlatformAdmin />}>
                  <Route path="accounts" element={<AdminAccounts />} />
                  <Route path="system" element={<AdminSystem />} />
                  <Route path="flags" element={<AdminFeatureFlags />} />
                  <Route path="whatsapp" element={<AdminWhatsApp />} />
                  <Route path="logs" element={<AdminLogs />} />
                  <Route path="jobs" element={<AdminJobs />} />
                  <Route path="health" element={<AdminHealth />} />
                </Route>
              </Route>
            </Route>
          </Route>
        </Route>
        {/* Unknown paths fall back to the landing rather than the auth-
            gated dashboard so unauth visitors land on the public face. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </HashRouter>
  );
}

function RequireAuth() {
  const isAuthed = useAgentStore((s) => !!s.current);
  // Hydrate conversations + visitors from the server the first time we
  // mount as an authenticated user. The hydration helper guards against
  // re-running on every component remount.
  useEffect(() => {
    if (!isAuthed) return;
    void hydrateFromServer();
    void refreshFeatureFlags();
    // Pick up any recording chunks left behind by a prior tab or crash and
    // push them now that the agent is back online.
    void resumeOrphans();
    // Cache which sessions have recordings on the server for the monitor
    // indicator + Conversation panel.
    void refreshRecordings();
    // Re-assert availability on every (re)connect: the server treats a newly
    // connected agent as available, so without this an agent who set themselves
    // offline would quietly come back online after a reconnect or a deploy.
    // Flags are re-read on the same signal, so a change made while this tab
    // was open shows up after the next reconnect (or deploy) at the latest.
    const off = onBusConnectionChange((state) => {
      if (state !== 'open') return;
      republishAvailability();
      void refreshFeatureFlags();
    });
    return off;
  }, [isAuthed]);
  if (!isAuthed) return <Navigate to="/login" replace />;
  return <Outlet />;
}

function RequireAdmin() {
  const role = useAgentStore((s) => s.current?.role);
  // The platform operator and account-admins get the admin surface; the
  // server scopes what an account-admin sees to its own account.
  if (role !== 'super-admin' && role !== 'account-admin') return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

/** Platform-wide settings (AI provider, WhatsApp number, jobs, global logs,
 *  tenants) — never an account-admin's to change. */
function RequirePlatformAdmin() {
  const role = useAgentStore((s) => s.current?.role);
  if (role !== 'super-admin') return <Navigate to="/admin/sites" replace />;
  return <Outlet />;
}

// Insights surfaces (Analytics, Contacts) are open to ANY signed-in agent —
// the server scopes what each one sees per site (admins see everything; plain
// agents see site analytics + only the contacts whose chats they handled).
function RequireInsights() {
  const role = useAgentStore((s) => s.current?.role);
  if (!role) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

/** Routes behind a feature flag, evaluated by the server for the agent's
 *  account. Renders nothing until the flags have loaded, so a deep link to a
 *  default-off feature doesn't flash before redirecting. */
function RequireFeature({ flag }: { flag: FeatureFlagKey }) {
  const loaded = useFeatureFlagsStore((s) => s.loaded);
  const enabled = useFeatureFlag(flag);
  if (!loaded) return null;
  if (!enabled) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

function RedirectIfLoggedIn({ page }: { page: React.ReactElement }) {
  const isAuthed = useAgentStore((s) => !!s.current);
  if (isAuthed) return <Navigate to="/dashboard" replace />;
  return page;
}
